import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaFileAlt, FaTags, FaUsers, FaCog, FaPlug, FaAddressCard } from 'react-icons/fa';
import { getLabelMessage } from '../../../const/labelService';
import { getEnabledSettingsTabs } from '../../../utils/settingsTabs';
import { designTokens } from '../foundation/DesignTokens';
import { Drawer, DrawerSectionTitle, DrawerList, DrawerItem } from './Drawer';

// Styled sidebar title matching HTML reference (.sidebar-title)
const SidebarTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.25rem;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: ${designTokens.spacing.xl};
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};

  @media (max-width: 900px) {
    font-size: 1.1rem;
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Enhanced drawer item with better styling
const NavItem = styled(DrawerItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${designTokens.radius.md};
  color: ${designTokens.colors.gray[700]};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;

  &:hover:not(:disabled) {
    background-color: ${designTokens.colors.gray[50]};
    color: ${designTokens.colors.gray[900]};
  }

  ${(props) =>
    props.active &&
    `
    background-color: ${designTokens.colors.primary[50]};
    color: ${designTokens.colors.primary[600]};
    font-weight: 600;
    border-color: ${designTokens.colors.primary[100]};
  `}

  @media (max-width: 900px) {
    font-size: 0.87rem;
    padding: 10px 14px;
    gap: 10px;
  }
`;

// Icon wrapper
const NavIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.active ? 1 : 0.7)};
  transition: opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 900px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// Settings navigation items with icons
const iconMap = {
  whatsappTemplates: FaFileAlt,
  tagsTab: FaTags,
  rolesTab: FaUsers,
  dataSheetConfigurationTab: FaAddressCard,
  configTab: FaCog,
  integrationsTab: FaPlug,
};

const getNavigationItemConfig = (tabKey, label) => {
  return {
    id: tabKey,
    label: label,
    Icon: iconMap[tabKey] || FaCog,
    key: tabKey,
  };
};

const SettingsNavigation = ({ activeView, onViewChange }) => {
  const userData = useSelector((state) => state.user.data);
  const enabledTabs = getEnabledSettingsTabs(userData.permissions);

  const lbls = getLabelMessage(enabledTabs);

  const navigationItems = enabledTabs.map((tabKey) =>
    getNavigationItemConfig(tabKey, lbls[tabKey])
  );

  return (
    <Drawer>
      <SidebarTitle>Configuración</SidebarTitle>

      <DrawerList>
        {navigationItems.map((item) => (
          <NavItem
            key={item.id}
            active={activeView === item.id}
            onClick={() => onViewChange(item.id)}>
            <NavIcon active={activeView === item.id}><item.Icon /></NavIcon>
            {item.label}
          </NavItem>
        ))}
      </DrawerList>
    </Drawer>
  );
};

export default SettingsNavigation;
